import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { Package, AlertTriangle, Check, Send } from 'lucide-react';
import { toast } from 'sonner';

const poItems = [
  { title: 'The Wonders of Coding', qty: 184, price: 18.50, stock: 500, stockOk: true, status: 'upcoming' },
  { title: 'Between Two Worlds', qty: 119, price: 16.80, stock: 300, stockOk: true, status: 'upcoming' },
  { title: 'Climate Change & You', qty: 208, price: 14.20, stock: 400, stockOk: true, status: 'upcoming' },
  { title: 'Body & Mind: Teen Guide', qty: 287, price: 12.90, stock: 0, stockOk: false, status: 'sourcing', issue: 'Out of print' },
];

export default function SupplierPOFulfilment() {
  const handleMarkDispatched = () => {
    toast.success('Books marked as dispatched');
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-3">
          <Package className="w-7 h-7 text-[#7A4020]" />
          Purchase Order Fulfilment
        </h1>
      </div>

      {/* Active PO */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading">PO-SUP-OT-CDS-C1-2028</CardTitle>
              <CardDescription>
                From: Civica | Issued: 16 Sep 2026 | Deliver to: Civica Processing Centre
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-lg px-4 py-2">
              4,820 books | 38 titles | $78,246
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-4">Delivery deadline: Oct 2027</p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead>Price (locked)</TableHead>
                <TableHead>Stock Status</TableHead>
                <TableHead>Fulfilment</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {poItems.map((item, idx) => (
                <TableRow key={idx} className="table-row-striped">
                  <TableCell className="font-medium">{item.title}</TableCell>
                  <TableCell>{item.qty}</TableCell>
                  <TableCell>${item.price.toFixed(2)}</TableCell>
                  <TableCell>
                    {item.stockOk ? (
                      <span className="text-emerald-600">✓ {item.stock} avail.</span>
                    ) : (
                      <span className="text-amber-600">⚠️ {item.issue}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {item.status === 'upcoming' ? '⏳ Upcoming' : '⏳ Alt. sourcing'}
                  </TableCell>
                  <TableCell>
                    {item.stockOk ? (
                      <Badge variant="outline">Oct 2027</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700">See below</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className="text-sm text-slate-500 mt-4">Showing 4 of 38 titles</p>
        </CardContent>
      </Card>

      {/* Out of Print Alert */}
      <Card className="border-amber-200 bg-amber-50/50">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Out of Print — "Body & Mind: Teen Guide"
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span>Contracted price:</span>
              <span>$12.90</span>
            </div>
            <div className="flex justify-between">
              <span>Qty needed:</span>
              <span>287 copies</span>
            </div>
            <div className="p-3 bg-white rounded-lg border">
              <p className="font-medium mb-2">OpenTrolley sourcing plan:</p>
              <p className="text-slate-600">
                40 copies sourced from Kinokuniya wholesale. 247 copies sourced from Amazon Books wholesale. 
                Total 287 @ $12.90 per copy. OpenTrolley absorbs any cost difference.
              </p>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700">
              ✓ Alternative source confirmed — no extra cost to schools/MOE HQ
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Dispatch Update */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Dispatch Update</CardTitle>
          <CardDescription>Update when books are ready to send to Civica</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <Label>PO reference</Label>
              <Input value="PO-SUP-OT-CDS-C1-2028" disabled className="mt-1" />
            </div>
            <div>
              <Label>Books dispatched this batch</Label>
              <Input type="number" placeholder="Enter qty" className="mt-1" />
            </div>
            <div>
              <Label>Dispatch date</Label>
              <Input type="date" className="mt-1" />
            </div>
            <div>
              <Label>Courier tracking reference</Label>
              <Input placeholder="Enter tracking #" className="mt-1" />
            </div>
          </div>
          <Button onClick={handleMarkDispatched} className="bg-[#7A4020] hover:bg-[#5A3018]" data-testid="mark-dispatched-btn">
            <Check className="w-4 h-4 mr-2" />
            Mark as Dispatched — Notify Civica
          </Button>
        </CardContent>
      </Card>

      {/* Dispatch Log */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Dispatch Log</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <div>
                <p className="font-medium">PO-SUP-OT-PIL-C1 | 300 books</p>
                <p className="text-sm text-slate-500">20 Jul 2026 | OT-DISPATCH-20260720</p>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700">✓ Civica received</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <div>
                <p className="font-medium">PO-SUP-OT-MK-2026 | 360 books</p>
                <p className="text-sm text-slate-500">20 Sep 2026 | OT-DISPATCH-20260920</p>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700">✓ Civica received</Badge>
            </div>
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div>
                <p className="font-medium">PO-SUP-OT-CDS-C1 | 4,820 books</p>
                <p className="text-sm text-slate-500">Oct 2027 (planned)</p>
              </div>
              <Badge variant="outline">⏳ Upcoming</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
